import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ActivityMetricsV2BackendMode,
  ActivityMetricsV2ChunkDiagnostic,
  ActivityMetricsV2Diagnostics,
  ActivityMetricsV2ExportResponse,
  ActivityMetricsV2SourceCount,
  ActivityMetricsV2StoreCleanupResponse,
  ActivityMetricsV2StoreCoverage,
  ActivityMetricsV2StoreEventCount,
  ActivityMetricsV2StoreReportRunStatusCount,
  ActivityMetricsV2StoreSetupResponse,
  ActivityMetricsV2StoreStatus,
  ActivityMetricsV2StoreSyncResponse,
  ActivityMetricsV2StoreSyncRun,
  ActivityMetricsV2StoreSyncState,
} from '../../../../shared/models/activity-metrics-v2.model';

@Component({
  selector: 'app-agent-performance-activity-metrics-technical-overview-widget',
  templateUrl: './agent-performance-activity-metrics-technical-overview-widget.component.html',
  styleUrls: ['./agent-performance-activity-metrics-technical-overview-widget.component.scss']
})
export class AgentPerformanceActivityMetricsTechnicalOverviewWidgetComponent {
  @Input() collapsedSections: Record<string, boolean> = {};
  @Input() backendMode: ActivityMetricsV2BackendMode = 'event_store';
  @Input() storeStatus: ActivityMetricsV2StoreStatus | null = null;
  @Input() storeSyncResult: ActivityMetricsV2StoreSyncResponse | null = null;
  @Input() storeCleanupResult: ActivityMetricsV2StoreCleanupResponse | null = null;
  @Input() storeSetupResult: ActivityMetricsV2StoreSetupResponse | null = null;
  @Input() storeErrorMessage: string | null = null;
  @Input() storeSyncProgressMessage: string | null = null;
  @Input() isStoreStatusLoading = false;
  @Input() isStoreSettingUp = false;
  @Input() isStoreSyncing = false;
  @Input() isStoreCleaning = false;
  @Input() storeCoverage: ActivityMetricsV2StoreCoverage | null = null;
  @Input() storeCoverageAlertClass = 'alert alert-info';
  @Input() storeCoverageHeadline = '';
  @Input() storeCoverageDetail = '';
  @Input() storeCoverageActionLabel = 'Sync Current Window';
  @Input() shouldShowStoreCoverageAction = false;
  @Input() storeCoverageIndexedThrough: string | null = null;
  @Input() storeCoverageGapLabel: string | null = null;
  @Input() latestStoreSyncRun: ActivityMetricsV2StoreSyncRun | null = null;
  @Input() latestStoreSyncRuns: ActivityMetricsV2StoreSyncRun[] = [];
  @Input() storeSyncState: ActivityMetricsV2StoreSyncState[] = [];
  @Input() storeSourceCounts: ActivityMetricsV2StoreEventCount[] = [];
  @Input() storeReportRunStatusCounts: ActivityMetricsV2StoreReportRunStatusCount[] = [];
  @Input() storeTotalEvents = 0;
  @Input() diagnostics: ActivityMetricsV2Diagnostics | null = null;
  @Input() diagnosticsSourceRows = 0;
  @Input() diagnosticsWarnings: string[] = [];
  @Input() diagnosticsChunkRows: ActivityMetricsV2ChunkDiagnostic[] = [];
  @Input() exportPreview: ActivityMetricsV2ExportResponse | null = null;
  @Input() storeSyncCompletedModules = 0;
  @Input() storeSyncTotalModules = 0;

  @Output() sectionToggled = new EventEmitter<string>();
  @Output() refreshStatus = new EventEmitter<void>();
  @Output() setupSources = new EventEmitter<void>();
  @Output() syncCurrentWindow = new EventEmitter<void>();
  @Output() cleanCurrentWindow = new EventEmitter<void>();
  @Output() cleanAllIndexed = new EventEmitter<void>();
  @Output() openHelp = new EventEmitter<string>();

  isSectionCollapsed(sectionKey: string): boolean {
    return !!this.collapsedSections[sectionKey];
  }

  toggleSection(sectionKey: string): void {
    this.sectionToggled.emit(sectionKey);
  }

  formatStageLabel(stageKey: string | null | undefined): string {
    if (!stageKey) {
      return '-';
    }

    return stageKey
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  formatBackendMode(mode: ActivityMetricsV2BackendMode | null | undefined): string {
    switch (mode) {
      case 'event_store':
        return 'Reporting Tables';
      case 'live':
        return 'Live Tables';
      case 'auto':
      default:
        return 'Auto';
    }
  }

  formatMilliseconds(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '-';
    }

    if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}s`;
    }

    return `${Math.round(value)}ms`;
  }

  trackBySourceCount(index: number, item: ActivityMetricsV2StoreEventCount | ActivityMetricsV2SourceCount): string {
    return item.source_key;
  }

  trackBySyncRun(index: number, item: ActivityMetricsV2StoreSyncRun): number {
    return item.activity_metrics_v2_sync_run_id;
  }

  trackBySyncState(index: number, item: ActivityMetricsV2StoreSyncState): string {
    return item.source_key;
  }

  trackBySourceKey(index: number, item: string): string {
    return item;
  }

  trackByReportRunStatus(index: number, item: ActivityMetricsV2StoreReportRunStatusCount): string {
    return item.status;
  }
}
